// src/components/AmendmentsPanel.jsx
// "Amendments" view on the Forecasts tab: bills that were modified on their
// way to passage, grouped by the parent measure, with what kind of change got
// them through (funding adjusted, scope narrowed, protective language added).
//
// Amendments are rare in raw count but concentrated where it matters — the
// record's most contested ordinances passed only after amendment — so this is
// the page that shows HOW divided measures get unblocked. Same live-fetch
// pattern as every other panel: recomputed from the record on each visit.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { fetchAllRows } from '../utils/fetchAllRows';
import { buildSignals, AMEND_TYPES } from './forecastSignals';
import './AmendmentsPanel.css';

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return String(iso);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
};

const TYPE_LABEL = Object.fromEntries(AMEND_TYPES.map((t) => [t.id, t.label]));

function AmendmentsPanel({ districtId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [votings, setVotings] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await fetchAllRows(() =>
        supabase
          .from('Votings')
          .select('id, voteDate, category, issue, outcome, votes')
          .eq('districtId', districtId)
          .order('voteDate', { ascending: false })
          .order('id', { ascending: false })
      );
      if (cancelled) return;
      setVotings(error ? [] : data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  const view = useMemo(() => {
    if (!votings.length) return null;
    const S = buildSignals({ votings });
    if (!S.amendments.length) return { empty: true, recorded: S.recorded.length };

    // Group a bill's amendment fight together: same meeting date + item number
    // ("Adopt Dorsey amendment to item 41" and "Pass item 41 as amended" are
    // one story). Votes without an item number stand alone.
    const groups = new Map();
    for (const a of S.amendments) {
      const key = a.itemNo ? `${a.date}#${a.itemNo}` : `solo#${a.id}`;
      if (!groups.has(key)) {
        groups.set(key, { date: a.date, itemNo: a.itemNo, votes: [], types: new Set(), divided: false });
      }
      const g = groups.get(key);
      g.votes.push(a);
      g.types.add(a.amendType);
      if (a.tally.no > 0) g.divided = true;
    }
    const rows = Array.from(groups.values()).sort((a, b) => {
      if (a.divided !== b.divided) return a.divided ? -1 : 1;
      return b.date.localeCompare(a.date);
    });

    const typeCounts = new Map();
    for (const a of S.amendments) {
      typeCounts.set(a.amendType, (typeCounts.get(a.amendType) || 0) + 1);
    }
    const dividedAmend = S.amendments.filter((a) => a.tally.no > 0).length;

    return {
      rows,
      total: S.amendments.length,
      dividedAmend,
      dividedTotal: S.divided.length,
      recorded: S.recorded.length,
      typeCounts: Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1]),
    };
  }, [votings]);

  if (loading) return <div className="am-card am-status">Reading the voting record…</div>;
  if (!view) return <div className="am-card am-status">No recorded votes yet for this district.</div>;
  if (view.empty) {
    return (
      <div className="am-card am-status">
        None of the {view.recorded} recorded votes involved an amendment — every
        measure passed or failed as introduced.
      </div>
    );
  }

  return (
    <div className="am-wrap">
      <div className="am-card">
        <h3 className="am-title">Amendments — how contested bills get through</h3>
        <p className="am-sub">
          {view.total} of {view.recorded} recorded votes involved an amendment —
          rare overall, but {view.dividedAmend} of the {view.dividedTotal} divided
          votes were amendment fights. The record&rsquo;s most contested ordinances
          passed <strong>only after</strong> being modified; this is what changed.
        </p>
        <div className="am-types">
          {view.typeCounts.map(([tid, n]) => (
            <span className="am-type-chip" key={tid}>
              {TYPE_LABEL[tid] || tid} <strong>{n}</strong>
            </span>
          ))}
        </div>
      </div>

      {view.rows.map((g) => (
        <div className={`am-card am-group${g.divided ? ' am-group-divided' : ''}`} key={`${g.date}#${g.itemNo || g.votes[0].id}`}>
          <div className="am-group-head">
            <span className="am-date">{fmtDate(g.date)}</span>
            {g.itemNo && <span className="am-item">Item {g.itemNo}</span>}
            {g.divided
              ? <span className="am-flag am-flag-divided">amendment fight</span>
              : <span className="am-flag">routine amendment</span>}
            <span className="am-type-list">
              {[...g.types].map((t) => TYPE_LABEL[t] || t).join(' · ')}
            </span>
          </div>
          <ul className="am-votes">
            {g.votes
              .slice()
              .sort((a, b) => a.id - b.id)
              .map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    className={`am-tally${v.tally.no > 0 ? ' am-tally-split' : ''}${v.tally.yes <= v.tally.no ? ' am-tally-failed' : ''}`}
                    title="See the full roll call on the Voting tab"
                    onClick={() => navigate(`?tab=voting#vote-${v.id}`)}
                  >
                    {v.tally.yes}–{v.tally.no}
                  </button>
                  <span className="am-issue">
                    {v.issue}
                    {v.tally.yes <= v.tally.no && <em className="am-failed-note"> — failed</em>}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      ))}

      <p className="am-note">
        Amendment votes are found by their wording in the vote record (&ldquo;as
        amended&rdquo;, &ldquo;adopt … amendment&rdquo;), and change types are
        keyword-classified from each amendment&rsquo;s own description — both are
        approximate. A bill amended without a separate recorded vote will not
        appear here. This page recomputes from the record on every visit, so new
        meetings appear automatically once processed.
      </p>
    </div>
  );
}

export default AmendmentsPanel;
